import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { indigo } from '@mui/material/colors';

import { Loader } from 'ui';
import { EmptyDataLabel } from 'ui/Labels';
import { GougeChart } from 'ui/Charts';
import { FlatScoreComparisonChart } from 'modules/ESG';
import { ViewPeerGroup } from 'modules/common';
import { getHexColorByScoreOverview } from 'shared/functions';
import { useReportPage } from 'shared/hooks';
import { fetchPeersNames } from 'services';

const scoreMax = 10;

const useStyles = makeStyles({
  divider: {
    alignSelf: 'stretch',
    height: 'auto',
  },
  itemWrapper: {
    maxWidth: '33.2%',
  },
  score: {
    fontSize: 40,
    fontWeight: 500,
  },
  scoreTotal: {
    fontSize: 18,
    fontWeight: 500,
  },
  legendText: {
    fontSize: useReportPage ? 10 : 14,
  },
  flatScoreComparisonChartWrapper: {
    marginLeft: useReportPage ? 60 : 100,
    marginRight: useReportPage ? 60 : 100,
  },
});

const ESGScoreComparison = ({ scores, id, loading }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <Paper>
      <Box p={2}>
        <Grid container>
          <Grid item xs={10}>
            <Typography variant="h6">{t('Peer group comparison')}</Typography>
          </Grid>
          {!useReportPage ? (
            <Grid container item xs={2} justifyContent="flex-end">
              <ViewPeerGroup id={id} fetchPeersNames={fetchPeersNames} />
            </Grid>
          ) : null}
        </Grid>
      </Box>
      <Box p={2}>
        {loading ? (
          <Loader center />
        ) : (
          <>
            {scores.length === 0 ? (
              <Box py={2}>
                <EmptyDataLabel label={t('There are no data available yet.')} />
              </Box>
            ) : (
              <>
                <Box py={2}>
                  <Divider />
                </Box>
                <Grid container>
                  {scores.map(
                    (
                      {
                        title,
                        score,
                        worstScore,
                        averageScore,
                        bestScore,
                        companyImpact,
                        supplyChainImpact,
                        averageImpact,
                        bestImpact,
                        worstImpact,
                      },
                      index,
                    ) => (
                      <Fragment key={title}>
                        <Grid item xs className={classes.itemWrapper}>
                          <Box py={2}>
                            <GougeChart
                              value={score}
                              best={bestScore}
                              worst={worstScore}
                              average={averageScore}
                              score={
                                <Box>
                                  <Typography className={classes.score} component="span">
                                    {score}
                                  </Typography>
                                  <Typography className={classes.scoreTotal} component="span">
                                    /{scoreMax}
                                  </Typography>
                                </Box>
                              }
                              title={title}
                              color={getHexColorByScoreOverview(score)}
                            />
                            <div className={classes.flatScoreComparisonChartWrapper}>
                              <FlatScoreComparisonChart
                                lowest={worstImpact}
                                average={averageImpact}
                                highest={bestImpact}
                                companyImpact={companyImpact}
                                supplyChainImpact={supplyChainImpact}
                              />
                            </div>
                          </Box>
                        </Grid>
                        {index < scores.length - 1 && (
                          <Divider className={classes.divider} orientation="vertical" />
                        )}
                      </Fragment>
                    ),
                  )}
                </Grid>
              </>
            )}
          </>
        )}
        <Box display="flex">
          <Box
            width={useReportPage ? 14 : 40}
            height={useReportPage ? 7 : 20}
            marginTop={useReportPage ? '4px' : 0}
            borderRadius="5px"
            marginRight={1}
            style={{ backgroundColor: indigo[400] }}
          />
          <Typography variant="body2" className={classes.legendText}>
            {t('Peer group')}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

ESGScoreComparison.propTypes = {
  loading: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  scores: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      score: PropTypes.number.isRequired,
      worstScore: PropTypes.number.isRequired,
      averageScore: PropTypes.number.isRequired,
      bestScore: PropTypes.number.isRequired,
    }),
  ).isRequired,
};

export default ESGScoreComparison;
